import { BookedSlotGroup } from "./types.ts";
import { getTeacherSlotStats } from "./getTeacherSlotStats.ts";
import dayjs from "dayjs";

function SlotTimeRange({ group }: { group: BookedSlotGroup }) {
  return (
    <p className={"text-xs text-slate-500"}>
      {dayjs(group.startUtc).format("HH:mm")} –{" "}
      {dayjs(group.endUtc).format("HH:mm")}
    </p>
  );
}

export function BookedClassRoomSlotItem({
  group,
  onClick,
  selectedTeacherId,
  candidateClassRoomIds,
}: {
  group: BookedSlotGroup;
  onClick: () => void;
  selectedTeacherId?: string | null;
  candidateClassRoomIds?: Set<string>;
}) {
  const count = group.classRooms.length;
  const availableCount = group.availableTeacherIds.length;

  if (!selectedTeacherId) {
    if (count === 0 && availableCount === 0) return null;

    if (count === 0) {
      return (
        <button
          onClick={onClick}
          className={
            "w-full text-left border-2 border-slate-300 bg-white p-2 rounded-lg hover:bg-slate-50 transition-colors space-y-1"
          }
        >
          <p className={"text-sm font-semibold text-slate-500"}>
            {availableCount} available
          </p>
          <SlotTimeRange group={group} />
        </button>
      );
    }

    return (
      <button
        onClick={onClick}
        className={
          "w-full text-left border-2 border-primary bg-primary bg-opacity-10 p-2 rounded-lg hover:bg-opacity-20 transition-colors space-y-1"
        }
      >
        <p className={"text-sm font-semibold text-primary"}>
          {count} {count === 1 ? "classroom" : "classrooms"}
        </p>
        {availableCount > 0 && (
          <p className={"text-sm font-medium text-slate-500"}>
            {availableCount} available
          </p>
        )}
        <SlotTimeRange group={group} />
      </button>
    );
  }

  const { assignedCount, candidateCount, isAvailable } = getTeacherSlotStats(
    group,
    { teacherId: selectedTeacherId, candidateClassRoomIds },
  );

  if (assignedCount === 0 && candidateCount === 0 && !isAvailable) return null;

  const isAvailableOnly =
    assignedCount === 0 && candidateCount === 0 && isAvailable;
  const isCandidateOnly = assignedCount === 0 && candidateCount > 0;
  const isMixed = assignedCount > 0 && candidateCount > 0;

  const borderClass = isAvailableOnly
    ? "border-slate-300"
    : isCandidateOnly
      ? "border-amber-400"
      : isMixed
        ? "border-purple-400"
        : "border-primary";

  const bgClass = isAvailableOnly
    ? "bg-white"
    : isCandidateOnly
      ? "bg-amber-50"
      : isMixed
        ? "bg-purple-50"
        : "bg-primary bg-opacity-10";

  const hoverClass = isAvailableOnly
    ? "hover:bg-slate-50"
    : isCandidateOnly
      ? "hover:bg-amber-100"
      : isMixed
        ? "hover:bg-purple-100"
        : "hover:bg-opacity-20";

  return (
    <button
      onClick={onClick}
      className={`w-full text-left border-2 ${borderClass} ${bgClass} ${hoverClass} p-2 rounded-lg transition-colors space-y-1`}
    >
      {assignedCount > 0 && (
        <p className={"text-sm font-semibold text-primary"}>
          {assignedCount} assigned
        </p>
      )}
      {candidateCount > 0 && (
        <p className={"text-sm font-semibold text-amber-600"}>
          {candidateCount} as candidate
        </p>
      )}
      {isAvailable && (
        <p className={"text-sm font-medium text-slate-500"}>available</p>
      )}
      {isMixed && (
        <p className={"text-xs text-purple-500 font-medium"}>mixed</p>
      )}
      <SlotTimeRange group={group} />
    </button>
  );
}
